import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Table, Accordion } from '@/ui'
import { DateTime } from 'luxon'

import {
  adminGetUserAllocationGuides,
  adminGetUserAllocationRules,
} from '@/selectors/allocationGuideSelectors'
import { fetchAdminUserAllocationGuides } from '@/actions/allocationGuideActions'
import { User } from '@/reducers/auth/userReducer'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TextColors, TypographyStyles } from '@/styles'

export const AllocationGuidesCard = ({ user }: { user: User }) => {
  const dispatch = useAppDispatch()
  const guides = useReselector(adminGetUserAllocationGuides, user.id)
  const rules = useReselector(adminGetUserAllocationRules, user.id)

  useEffect(() => {
    dispatch(fetchAdminUserAllocationGuides(user.id))
  }, [dispatch, user.id])

  const renderRules = () => {
    if (Object.keys(rules).length === 0) {
      return (
        <div>
          <p>User has not set up allocation guides yet</p>
        </div>
      )
    } else {
      return (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell colSpan={3}>Current Guide:</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {Object.values(rules).map(({ id, ruleName, percentage }) => (
              <Table.Row key={`rule-${id}`}>
                <Table.Cell>{ruleName}</Table.Cell>
                <Table.Cell>{percentage}%</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )
    }
  }
  const tableBody = () => {
    if (Object.keys(guides).length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={3} textAlign="center">
            None yet
          </Table.Cell>
        </Table.Row>
      )
    } else {
      return Object.values(guides).map(({ id, date, compiledOn }) => {
        return (
          <Table.Row key={`guide${id}`}>
            <Table.Cell>
              {DateTime.fromISO(date).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)}
            </Table.Cell>
            <Table.Cell>
              {DateTime.fromISO(compiledOn).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_LONG
              )}
            </Table.Cell>
            <Table.Cell>
              <Link
                to={`/admin/finances/records/${user.id}/allocation-guides/${id}`}
              >
                View Guide
              </Link>
            </Table.Cell>
          </Table.Row>
        )
      })
    }
  }

  return (
    <Card className="w-full">
      <Text variant={TypographyStyles.H2}>Allocation Guides</Text>
      <br />
      {renderRules()}
      <br />
      <Accordion type="single" collapsible className="w-full" variant="text">
        <Accordion.Item value="history">
          <Accordion.Trigger className="text-left w-full">
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.LINK}>
              Click to view All Allocation Guide History
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={6}>Month</Table.HeaderCell>
                  <Table.HeaderCell width={6}>Compiled On</Table.HeaderCell>
                  <Table.HeaderCell width={4}>Action</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{tableBody()}</Table.Body>
            </Table>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  )
}
